import { EMPTY_COUNTRY, EMPTY_REGION } from '@front-app/components/inputs/CountryPickerData';
import { STATUS } from '@front-app/constants/state';
import { fromEnglishString } from '@front-app/modules/organization/util/convert';
import { parseApiSrc } from '@front-app/store/util/image';
import { normalizePotentialHexString } from '@front-app/util/color';
import { base64ToHtml } from '@front-app/util/string';
import {
	EventCurrency,
	EventLocation,
	EventPrice,
	FullEvent,
	FullEventResponse,
	PriceOption,
} from './EventsApi/EventModel';
import {
	Content,
	ContentResponse,
	ContentResponseV2,
	Contents,
	ContentsResponse,
	ContentsResponseV2,
	ContentsV2,
	ContentType,
	ContentV2,
	Discussion,
	DiscussionContentItem,
	DiscussionContentResponse,
	DiscussionMessage,
	DiscussionMessageResponse,
	DiscussionResponse,
	FileContentItem,
	FileContentResponse,
	LinkContentItem,
	LinkContentResponse,
} from './GroupsApi/DiscussionModel';
import {
	FullOrganization,
	FullOrganizationResponse,
} from './OrganizationsApi/OrganizationModel';
import {
	Address,
	AddressResponse,
	AddressWithContactInfo,
	AddressWithContactInfoResponse,
	ContactInfo,
	CountryCode,
	RegionCode,
} from './SharedModels';
import {
	Association,
	AssociationResponse,
	FullUser,
	FullUserOccupation,
	FullUserOccupationResponse,
	FullUserResponse,
	GroupMember,
	GroupMemberAdminResponse,
	GroupMemberCounts,
	GroupMemberCountsResponse,
	GroupMemberResponse,
	GroupMemberWithEmail,
	ProfessionalExperienceRequest,
	UserDetailRole,
	UserDetailRoleApi,
	UserRole,
} from './UserApi/UserModel';

export function parseContentsV2(sourceContents: ContentsResponseV2): ContentsV2 {
	return {
		contents: sourceContents.contents.map(parseContentV2),
		// comma delimited list of already retrieved discussion IDs. Returned with a previously paged list of Contents
		bufferedMessageIds: sourceContents.bufferedMessageIds,
		bufferedFileIds: sourceContents.bufferedFileIds,
	};
}

export function parseContentV2(content: ContentResponseV2): ContentV2 {
	const { type } = content;
	switch (type) {
	case ContentType.LINK:
		return parseLinkContentResponse(content);
	case ContentType.FILE:
		return parseFileContentResponse(content);
	case ContentType.DISCUSSION:
		return parseDiscussionContentResponse(content);
	default:
		// eslint-disable-next-line
		const unwanted: never = type;
		throw new Error(`Unable to satisfy component validation request for type: ${unwanted}`);
	}
}

export function parseLinkContentResponse(content: LinkContentResponse): LinkContentItem {
	return {
		...content,
		creator: parseFullUserResponse(content.creator),
		metadata: {
			url: content.metadata.url,
			attachedToDiscussions: content.metadata.attachedToDiscussions?.map(parseDiscussion) ?? null,
		},
	};
}

export function parseFileContentResponse(content: FileContentResponse): FileContentItem {
	return {
		...content,
		creator: parseFullUserResponse(content.creator),
		metadata: {
			byteSize: content.metadata.byteSize,
			attachedToDiscussions: content.metadata.attachedToDiscussions?.map(parseDiscussion) ?? null,
		},
	};
}

export function parseDiscussionContentResponse(content: DiscussionContentResponse): DiscussionContentItem {
	return {
		...content,
		creator: parseFullUserResponse(content.creator),
		metadata: {
			discussion: parseDiscussion(content.metadata.discussion),
		},
	};
}

/**
 * @deprecated
 */
export function parseContents(sourceContents: ContentsResponse): Contents {
	return {
		contents: sourceContents.contents.map(parseContent),
		// comma delimited list of already retrieved discussion IDs. Returned with a previously paged list of Contents
		bufferedMessageIds: sourceContents.bufferedMessageIds,
		bufferedFileIds: sourceContents.bufferedFileIds,
	};
}

/**
 * @deprecated
 */
export function parseContent(content: ContentResponse): Content {
	return {
		...content,
		discussion: content.discussion ? parseDiscussion(content.discussion) : null,
		attachedToDiscussions: content.attachedToDiscussions ? content.attachedToDiscussions.map(parseDiscussion) : null,
		creator: parseFullUserResponse(content.creator),
	};
}

export function parseDiscussion(source: DiscussionResponse): Discussion {
	return {
		...source,
		followers: source.followers.map(parseFullUserResponse),
		messages: source.messages.map(parseDiscussionMessageResponse),
	};
}

export function parseDiscussionMessageResponse(source: DiscussionMessageResponse): DiscussionMessage {
	return {
		...source,
		sender: parseFullUserResponse(source.sender),
	};
}

export function parseFullEvent(raw: FullEventResponse): FullEvent {
	const address = {
		street1: raw.address?.street1 || '',
		street2: raw.address?.street2 || '',
		city: raw.address?.city || '',
		provStateRegion: (raw.address?.province as RegionCode) || EMPTY_REGION,
		country: (raw.address?.country as CountryCode) || EMPTY_COUNTRY,
		postalCode: raw.address?.postal || '',
		longitude: parseFloat(raw.address?.longitude || '0'),
		latitude: parseFloat(raw.address?.latitude || '0'),
		contactInfo: raw.address?.contactInfo || '',
		empty: false,
	};
	address.empty = raw.address ? isEmptyLocation(address) : true;
	const onlineMeeting = {
		videoType: raw.onlineMeeting.videoType,
		videoUrl: raw.onlineMeeting.videoUrl,
		videoButtonText: raw.onlineMeeting.videoButtonText,
		videoButtonColor: normalizePotentialHexString(raw.onlineMeeting.videoButtonColor, '#0a60a6'),
	};
	return {
		id: raw.id,
		name: raw.name,
		type: raw.type,
		description: raw.description, // HTML
		role: raw.role,
		bannerLink: raw.bannerLink || '',
		pageHtml: base64ToHtml(raw.pageHtml), // HTML
		termsAndConditionsHtml: base64ToHtml(raw.termsAndConditionsHtml), // HTML,
		lastVisited: raw.lastVisited === null ? null : new Date(raw.lastVisited),
		created: new Date(raw.created),
		categories: raw.categories,
		isPublished: raw.published,
		preferenceTokens: raw.preferenceTokens,
		paymentRequired: raw.paymentRequired,
		termsRequired: raw.termsRequired,
		startDate: new Date(raw.startDate),
		endDate: raw.endDate ? new Date(raw.endDate) : new Date(raw.startDate),
		address,
		allowRegistration: raw.allowRegistration,
		isInterested: raw.isInterested,
		venueImageSrc: parseApiSrc(raw.venueImageSrc),
		paymentId: raw.paymentId,
		eventTypes: raw.eventTypes,
		participants: raw.participants,
		streams: raw.streams,
		onlineMeeting,
		priceV2: parseEventPrice(raw.priceV2),
	};
}

function parseEventPrice(val: EventPrice): EventPrice {
	return {
		type: (val.type.toUpperCase() as PriceOption),
		numericValue: val.numericValue,
		currency: (val.currency as EventCurrency),
	};
}

export function parseFullUserResponse(user: FullUserResponse): FullUser {
	return {
		id: user.id,
		status: STATUS.SUCCESS,
		salutation: user.salutation,
		displayName: user.displayName,
		firstName: user.firstName,
		middleName: user.middleName,
		lastName: user.lastName,
		authenticated: user.authenticated,
		verified: user.verified,
		gender: user.gender,
		languages: user.languages || [],
		birthdayYear: user.birthdayYear || '',
		birthdayMonth: user.birthdayMonth || '',
		birthdayDay: user.birthdayDay || '',
		// Roles, etc.
		primaryRole: user.primaryRole ? parseDetailRole(user.primaryRole) : UserDetailRole.NONE,
		roles: user.roles?.roles.map(parseDetailRole) || [],
		credentials: user.credentials?.credentials || [],
		specialties: user.specialties?.specialties || [],
		interests: user.interests?.interests || [],
		// Summaries
		summaryForAll: base64ToHtml(user.summaryForAll),
		summaryForContacts: base64ToHtml(user.summaryForContacts),
		// Contact
		email: user.email,
		phone: user.phone || '',
		mobile: user.mobile || '',
		// Occupation
		currentOccupation: parseFullUserOccupationResponse(user.currentOccupation || {}),
		professionalExperiences: (user.professionalExperiences || []).map(parseFullUserOccupationResponse),
		associations: user.associations?.map(parseAssociation) || [],
	};
}

export const API_ROLE_TO_ROLE: Record<UserDetailRoleApi, UserDetailRole> = {
	[UserDetailRoleApi.CSR]: UserDetailRole.CSR,
	[UserDetailRoleApi.NURSE]: UserDetailRole.NURSE,
	[UserDetailRoleApi.PHYSICIAN]: UserDetailRole.PHYSICIAN,
	[UserDetailRoleApi.NURSE_PRACTITIONER]: UserDetailRole.NURSE_PRACTITIONER,
	[UserDetailRoleApi.THERAPIST]: UserDetailRole.THERAPIST,
	[UserDetailRoleApi.SOCIAL_SERVICE]: UserDetailRole.SOCIAL_SERVICE,
	[UserDetailRoleApi.DIETITIAN]: UserDetailRole.DIETITIAN,
	[UserDetailRoleApi.PHARMACIST]: UserDetailRole.PHARMACIST,
	[UserDetailRoleApi.OFFICE_ADMIN]: UserDetailRole.OFFICE_ADMINISTRATOR,
	[UserDetailRoleApi.OFFICE_ADMINISTRATOR]: UserDetailRole.OFFICE_ADMINISTRATOR,
	[UserDetailRoleApi.NOT_FOR_PROFIT]: UserDetailRole.NOT_FOR_PROFIT,
	[UserDetailRoleApi.SCIENTIST]: UserDetailRole.SCIENTIST,
	[UserDetailRoleApi.COMPANY_PROFESSIONAL]: UserDetailRole.COMPANY_PROFESSIONAL,
	[UserDetailRoleApi.INDUSTRY]: UserDetailRole.INDUSTRY,
	[UserDetailRoleApi.OTHER]: UserDetailRole.OTHER,
	[UserDetailRoleApi.ALL_MEMBERS]: UserDetailRole.ALL_MEMBERS,
};

export const ROLE_TO_API_ROLE: Record<Exclude<UserDetailRole, UserDetailRole.NONE>, UserDetailRoleApi> = {
	[UserDetailRole.CSR]: UserDetailRoleApi.CSR,
	[UserDetailRole.NURSE]: UserDetailRoleApi.NURSE,
	[UserDetailRole.PHYSICIAN]: UserDetailRoleApi.PHYSICIAN,
	[UserDetailRole.NURSE_PRACTITIONER]: UserDetailRoleApi.NURSE_PRACTITIONER,
	[UserDetailRole.THERAPIST]: UserDetailRoleApi.THERAPIST,
	[UserDetailRole.SOCIAL_SERVICE]: UserDetailRoleApi.SOCIAL_SERVICE,
	[UserDetailRole.DIETITIAN]: UserDetailRoleApi.DIETITIAN,
	[UserDetailRole.PHARMACIST]: UserDetailRoleApi.PHARMACIST,
	[UserDetailRole.OFFICE_ADMINISTRATOR]: UserDetailRoleApi.OFFICE_ADMINISTRATOR,
	[UserDetailRole.NOT_FOR_PROFIT]: UserDetailRoleApi.NOT_FOR_PROFIT,
	[UserDetailRole.SCIENTIST]: UserDetailRoleApi.SCIENTIST,
	[UserDetailRole.COMPANY_PROFESSIONAL]: UserDetailRoleApi.COMPANY_PROFESSIONAL,
	[UserDetailRole.INDUSTRY]: UserDetailRoleApi.INDUSTRY,
	[UserDetailRole.OTHER]: UserDetailRoleApi.OTHER,
	[UserDetailRole.ALL_MEMBERS]: UserDetailRoleApi.ALL_MEMBERS,
};

export function parseDetailRole(input: UserDetailRoleApi): UserDetailRole {
	return API_ROLE_TO_ROLE[input];
}

function parseFullUserOccupationResponse(occupation: Partial<FullUserOccupationResponse>): FullUserOccupation {
	const {
		employer,
		positionTitle,
		startMonth,
		startYear,
		endMonth,
		endYear,
	} = occupation;
	const address = employer?.address || {};
	return {
		id: occupation.id ?? -1,
		positionTitle: positionTitle || '',
		startMonth: parseNullInt(startMonth),
		startYear: parseNullInt(startYear),
		endMonth: parseNullInt(endMonth),
		endYear: parseNullInt(endYear),
		location: parseAddressWithContactInfo(occupation.location || {}),
		phone: '',
		employer: {
			id: employer?.id || -1,
			name: employer?.name || '',
			type: employer?.type || '',
			logoSrc: employer?.logoSrc || '',
			address: parseAddressWithContactInfo(address),
		},
	};
}

export function unparseProfessionalExperience(occupation: Partial<FullUserOccupation>): ProfessionalExperienceRequest {
	const {
		employer,
		positionTitle,
		startMonth,
		startYear,
		endMonth,
		endYear,
	} = occupation;
	return {
		id: occupation.id,
		positionTitle: positionTitle || '',
		startMonth: unparseMinusOneInt(startMonth),
		startYear: unparseMinusOneInt(startYear),
		endMonth: unparseMinusOneInt(endMonth),
		endYear: unparseMinusOneInt(endYear),
		location: {
			city: occupation.location?.city || '',
			province: occupation.location?.provStateRegion ?? null,
			country: occupation.location?.country ?? null,
		},
		employer: {
			name: employer?.name || '',
		},
	};
}

function parseAddress(address: AddressResponse = {}): Address {
	return {
		street1: address.street1 || '',
		street2: address.street2 || '',
		city: address.city || '',
		provStateRegion: address.province || null,
		country: address.country || null,
		postalCode: address.postal || '',
	};
}

function parseAddressWithContactInfo(address: AddressWithContactInfoResponse = {}): AddressWithContactInfo {
	return {
		...parseAddress(address),
		contactInfo: parseContactInfo(address.contactInfo),
	};
}

function parseContactInfo(info: Partial<ContactInfo> = {}): ContactInfo {
	return {
		phone: info.phone || '',
		extension: info.extension || '',
		mobile: info.mobile || '',
		fax: info.fax || '',
		webaddress: info.webaddress || '',
	};
}

function parseNullInt(v: string | null | undefined): number | null {
	if (v === null || v === undefined) {
		return null;
	}
	return parseInt(v, 10);
}

function unparseMinusOneInt(v: number | null | undefined): string {
	if (v === null || v === undefined) {
		return '-1';
	}
	return v.toString();
}

export function isEmptyLocation(loc: EventLocation): boolean {
	const {
		street1,
		street2,
		city,
		provStateRegion,
		country,
		postalCode,
	} = loc;
	return !street1 && !street2 && !city && (!provStateRegion || provStateRegion.trim() === ',') && !country && !postalCode;
}

export function parseOrganizationResponse(org: FullOrganizationResponse): FullOrganization {
	const { address } = org;
	const {
		street1,
		street2,
		city,
		province,
		country,
		postal,
		contactInfo,
	} = address;
	return {
		id: typeof org.id === 'string' ? parseInt(org.id, 10) : org.id,
		type: fromEnglishString(org.type),
		name: org.name,
		logoSrc: org.logoSrc,
		address: {
			street1: street1 || '',
			street2: street2 || '',
			city: city || '',
			country: country || null,
			postalCode: postal || '',
			provStateRegion: province || null,
			contactInfo: parseContactInfo(contactInfo),
		},
	};
}

export function parseAssociation(association: AssociationResponse): Association {
	return {
		associationId: association.associationId,
		organizationName: association.organizationName || '',
		orgType: association.orgType || '',
		subGroupName: association.subGroupName || '',
		location: parseAddress(association.location),
	};
}

export function parseGroupMember(resp: GroupMemberResponse): GroupMember {
	return {
		...resp,
		groupGrantedAccessDate: new Date(resp.groupGrantedAccessDate),
		groupJoinDate: new Date(resp.groupJoinDate),
		groupLastInviteDate: resp.groupLastInviteDate ? new Date(resp.groupLastInviteDate) : null,
	};
}

export function parseGroupMemberWithEmail(resp: GroupMemberAdminResponse): GroupMemberWithEmail {
	return {
		...resp,
		email: resp.email || '',
		groupGrantedAccessDate: new Date(resp.groupGrantedAccessDate),
		groupJoinDate: new Date(resp.groupJoinDate),
		groupLastInviteDate: new Date(resp.groupLastInviteDate),
	};
}

export function parseGroupmemberCounts(resp: GroupMemberCountsResponse): GroupMemberCounts {
	return {
		[UserRole.MEMBER]: resp.member,
		[UserRole.MODERATOR]: resp.moderator,
		[UserRole.ADMINISTRATOR]: resp.administrator,
		// Below are always 0 for non-admins
		[UserRole.DEPARTEE]: resp.departee,
		[UserRole.INVITEE]: resp.invitee,
		[UserRole.EXTERNAL_INVITEE]: resp.externalInvitee,
	};
}
